<template>
  <div class="page-container">
    <!-- 头部 -->
    <div class="page-header">
      <div>
        <h2 class="page-title">落地页</h2>
      </div>
      <div class="page-header-extra">
        <span>共 <b>{{ list.length }}</b> 个版本</span>
        <span class="divider">|</span>
        <span>启用中 <b>{{ statusCount(1) }}</b></span>
        <span class="divider">|</span>
        <span>停用 <b>{{ statusCount(0) }}</b></span>
      </div>
    </div>

    <!-- 搜索 + 操作 -->
    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="关键字">
            <el-input v-model="query.keyword" placeholder="版本名称/宣传语关键词" clearable style="width: 200px" />
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="query.status" placeholder="全部" clearable style="width: 120px">
              <el-option v-for="s in LAUNCH_STATUS" :key="s.value" :label="s.label" :value="s.value" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd">新增版本</el-button>
        </div>
      </div>
    </el-card>

    <!-- 表格 -->
    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="filteredList" border size="small" height="100%">
        <!-- 标识图 -->
        <el-table-column label="标识图" width="90" align="center">
          <template slot-scope="scope">
            <div v-if="scope.row.logo" class="logo-thumb">
              <el-image :src="scope.row.logo" :preview-src-list="[scope.row.logo]" fit="cover" style="width: 100%; height: 100%" />
            </div>
            <div v-else class="logo-thumb logo-thumb--empty">默认</div>
          </template>
        </el-table-column>
        <!-- 版本名称 -->
        <el-table-column prop="name" label="版本名称" min-width="120" show-overflow-tooltip>
          <template slot-scope="scope">
            <span>{{ scope.row.name }}</span>
            <el-tag v-if="scope.row.status === 1" size="mini" type="success" effect="plain" class="name-tag">使用中</el-tag>
          </template>
        </el-table-column>
        <!-- 宣传语 -->
        <el-table-column label="宣传语" min-width="220" show-overflow-tooltip>
          <template slot-scope="scope">{{ sloganText(scope.row.slogan) }}</template>
        </el-table-column>
        <!-- 跳转目标 -->
        <el-table-column label="跳转目标" min-width="180" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.jump_url">{{ scope.row.jump_url }}</span>
            <span v-else class="cell-muted">站内图文（了解更多展开）</span>
          </template>
        </el-table-column>
        <!-- 跳过秒数 -->
        <el-table-column label="跳过秒数" width="90" align="center">
          <template slot-scope="scope">{{ scope.row.skip_seconds }}s</template>
        </el-table-column>
        <!-- 状态 -->
        <el-table-column label="状态" width="80" align="center">
          <template slot-scope="scope">
            <el-tag :type="launchStatusTagType(scope.row.status)" size="mini" effect="plain">
              {{ launchStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <!-- 更新时间 -->
        <el-table-column prop="updated_at" label="更新时间" min-width="160" show-overflow-tooltip />
        <!-- 操作 -->
        <el-table-column label="操作" width="200" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button
              v-if="scope.row.status !== 1"
              type="text" size="mini"
              @click="handleEnable(scope.row)"
            >启用</el-button>
            <el-button
              v-else
              type="text" size="mini" class="text-warning-btn"
              @click="handleDisable(scope.row)"
            >停用</el-button>
            <el-button type="text" size="mini" class="text-danger-btn" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 编辑/新增弹框 -->
    <el-dialog
      :title="isEdit ? '编辑落地页' : '新增落地页'"
      :visible.sync="formVisible"
      width="780px"
      top="4vh"
      class="launch-form-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="100px" label-position="top">
        <div class="form-section">
          <div class="form-section-title">基础</div>
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item label="版本名称" prop="name">
                <el-input v-model="form.name" placeholder="后台区分用，如：国庆版、春节版" maxlength="64" show-word-limit />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="跳过按钮秒数" prop="skip_seconds">
                <el-input-number v-model="form.skip_seconds" :min="1" :max="60" controls-position="right" style="width: 100%" />
                <span class="form-tip">小程序开屏右上角「跳过」按钮出现的等待秒数</span>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="标识图" prop="logo">
            <image-uploader v-model="form.logo" />
            <span class="form-tip">开屏页顶部圆形标识；留空则使用小程序默认标识</span>
          </el-form-item>
        </div>

        <div class="form-section">
          <div class="form-section-title">宣传语</div>
          <el-form-item label="宣传语（开屏底部竖排展示，最多 4 条）">
            <div v-for="(s, i) in form.slogan" :key="i" class="slogan-row">
              <el-input v-model="form.slogan[i]" :maxlength="8" show-word-limit placeholder="如：文脉永传" class="slogan-input" />
              <el-button
                type="text" size="mini" class="text-danger-btn"
                :disabled="form.slogan.length <= 1"
                @click="removeSlogan(i)"
              >移除</el-button>
            </div>
            <el-button
              v-if="form.slogan.length < 4"
              type="text" size="mini" icon="el-icon-plus"
              @click="addSlogan"
            >添加一条</el-button>
          </el-form-item>
        </div>

        <div class="form-section">
          <div class="form-section-title">「了解更多」展开内容</div>
          <el-form-item label="图文正文" prop="content">
            <wechat-editor v-model="form.content" />
          </el-form-item>
          <el-form-item label="跳转目标" prop="jump_url">
            <el-input v-model="form.jump_url" placeholder="填写小程序内页路径，点「了解更多」直接跳转" maxlength="500" />
            <span class="form-tip">留空时点「了解更多」展开上方图文正文</span>
          </el-form-item>
        </div>

        <div class="form-section">
          <div class="form-section-title">状态</div>
          <el-form-item label="是否启用" prop="status">
            <el-radio-group v-model="form.status">
              <el-radio :label="1">启用</el-radio>
              <el-radio :label="0">停用</el-radio>
            </el-radio-group>
            <span class="form-tip">启用后小程序开屏即按此版本展示</span>
          </el-form-item>
        </div>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="formVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="submit">保 存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  LAUNCH_STATUS,
  launchStatusText, launchStatusTagType, sloganText
} from '@/mock/launch'
import {
  getLaunchList, createLaunch, updateLaunch, deleteLaunch, setLaunchStatus
} from '@/api/launch'
import ImageUploader from '@/components/ImageUploader.vue'
import WechatEditor from '@/components/WechatEditor.vue'

export default {
  name: 'LaunchPageManage',
  components: { ImageUploader, WechatEditor },
  data() {
    return {
      loading: false,
      list: [],
      query: { keyword: '', status: '' },
      formVisible: false,
      currentRow: null,
      submitting: false,
      form: this.emptyForm(),
      rules: {
        name: [{ required: true, message: '请输入版本名称', trigger: 'blur' }],
        skip_seconds: [{ required: true, message: '请填写跳过秒数', trigger: 'blur' }],
        status: [{ required: true, message: '请选择状态', trigger: 'change' }]
      },
      LAUNCH_STATUS
    }
  },
  computed: {
    isEdit() { return !!(this.currentRow && this.currentRow.id) },
    filteredList() {
      const kw = (this.query.keyword || '').trim()
      return this.list.filter(i => {
        if (kw) {
          const hay = (i.name || '') + this.sloganText(i.slogan)
          if (hay.indexOf(kw) === -1) return false
        }
        if (this.query.status !== '' && this.query.status !== null && i.status !== this.query.status) return false
        return true
      })
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    loadData() {
      this.loading = true
      getLaunchList().then(res => {
        if (res.code !== 200) return
        // 后端已按 启用中→更新时间倒序 排好
        this.list = res.data.list || []
      }).finally(() => {
        this.loading = false
      })
    },
    statusCount(val) { return this.list.filter(i => i.status === val).length },
    launchStatusText,
    launchStatusTagType,
    sloganText,
    handleSearch() {
      this.loadData()
    },
    resetSearch() {
      this.query = { keyword: '', status: '' }
      this.loadData()
    },
    handleAdd() {
      this.currentRow = null
      this.form = this.emptyForm()
      this.formVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handleEdit(row) {
      this.currentRow = Object.assign({}, row)
      const form = Object.assign(this.emptyForm(), row)
      // 宣传语保证是数组副本，避免直接改到列表行
      form.slogan = Array.isArray(row.slogan) ? row.slogan.slice() : []
      if (!form.slogan.length) form.slogan = ['']
      this.form = form
      this.formVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    addSlogan() {
      if (this.form.slogan.length < 4) this.form.slogan.push('')
    },
    removeSlogan(i) {
      if (this.form.slogan.length > 1) this.form.slogan.splice(i, 1)
    },
    handleEnable(row) {
      this.$confirm(`确认启用「${row.name}」？启用后小程序开屏按此版本展示。`, '启用确认', { type: 'warning' })
        .then(() => {
          return setLaunchStatus(row.id, { status: 1 }).then(res => {
            if (res.code === 200) {
              this.$message.success('已启用')
              this.loadData()
            }
          })
        }).catch(() => {})
    },
    handleDisable(row) {
      this.$confirm(`确认停用「${row.name}」？停用后小程序端不再使用此版本。`, '停用确认', { type: 'warning' })
        .then(() => {
          return setLaunchStatus(row.id, { status: 0 }).then(res => {
            if (res.code === 200) {
              this.$message.success('已停用')
              this.loadData()
            }
          })
        }).catch(() => {})
    },
    handleDelete(row) {
      this.$confirm(`确认删除「${row.name || '该版本'}」？`, '删除确认', { type: 'warning' })
        .then(() => {
          return deleteLaunch(row.id).then(res => {
            if (res.code === 200) {
              this.$message.success('已删除')
              this.loadData()
            }
          })
        }).catch(() => {})
    },
    submit() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        // 宣传语去掉空行
        const payload = Object.assign({}, this.form)
        payload.slogan = (payload.slogan || []).map(s => (s || '').trim()).filter(s => s)
        if (!payload.content && !payload.jump_url) {
          this.$message.warning('「了解更多」需要图文正文与跳转目标至少填一个')
          return
        }
        this.submitting = true
        const task = this.isEdit
          ? updateLaunch(this.currentRow.id, payload)
          : createLaunch(payload)
        task.then(res => {
          if (res.code !== 200) return
          this.$message.success(this.isEdit ? '已保存' : '已新增')
          this.formVisible = false
          this.loadData()
        }).finally(() => {
          this.submitting = false
        })
      })
    },
    emptyForm() {
      return {
        id: 0,
        name: '',
        logo: '',
        slogan: [''],
        content: '',
        jump_url: '',
        skip_seconds: 3,
        status: 0
      }
    }
  }
}
</script>

<style scoped>
.logo-thumb {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: #f5f6f8;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  border: 2px solid var(--primary);
}
.logo-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.logo-thumb--empty {
  color: #c0c4cc;
  font-size: 12px;
  border: 2px dashed #dcdfe6;
  background: #fafafa;
}
.name-tag {
  margin-left: 6px;
}
.cell-muted {
  color: #c0c4cc;
  font-size: 12px;
}

/* 宣传语多行动态行 */
.slogan-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.slogan-input {
  width: 240px;
}

/* 分段标题（与其他模块一致） */
.form-section {
  margin-bottom: 18px;
  padding-bottom: 4px;
}
.form-section-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--primary);
  border-left: 3px solid var(--primary);
  padding-left: 8px;
  margin: 16px 0 14px;
}
.form-tip {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

/* 弹框内整体样式（与全局弹框适配） */
.launch-form-dialog >>> .el-dialog__body {
  padding: 14px 30px 4px;
}
.launch-form-dialog >>> .wechat-editor {
  min-height: 320px;
}
</style>
